import { useEffect, useState } from "react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { useT } from "@/lib/i18n"
import { timeAgo } from "@/lib/utils"
import { Badge, Button, Card, CardBody, CardHeader, Input, Spinner, Toast } from "@/components/ui"
import { ShareModeControl, shareModeLabel } from "@/components/connector-share"
import type { KnowledgeCollectionOut, TeamMemberOut, TeamOut } from "@/lib/types"
import { BookOpenIcon, CheckIcon, PencilIcon, PlugIcon, SendIcon, Trash2Icon, UserMinusIcon, UsersIcon, XIcon } from "lucide-react"

export const Route = createFileRoute("/_app/team")({
  component: TeamPage,
})

function useTeam() {
  return useQuery({ queryKey: ["team"], queryFn: () => api<TeamOut>("/api/team") })
}

function TeamPage() {
  const t = useT()
  const team = useTeam()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t("team.title")}</h1>
        <p className="text-[13px] text-muted-foreground mt-1.5">{t("team.sub")}</p>
      </div>
      {team.isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
      {team.error && <Toast variant="destructive">{(team.error as Error).message}</Toast>}
      {team.data && !team.data.workspace && <NoWorkspace data={team.data} />}
      {team.data && team.data.workspace && <Workspace data={team.data} />}
    </div>
  )
}

// ---------- no workspace yet ----------

function NoWorkspace({ data }: { data: TeamOut }) {
  const t = useT()
  const qc = useQueryClient()
  const [name, setName] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => api<TeamOut>("/api/team", { method: "POST", body: { name } }),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ["team"] }); qc.invalidateQueries({ queryKey: ["my-limits"] }) },
    onError: (e) => setErr((e as Error).message),
  })
  const price = Math.round(data.seat_price_eur_month)

  return (
    <Card>
      <CardHeader title={t("team.intro.title")} accent="#C08CFF" />
      <CardBody className="space-y-4">
        <p className="text-sm text-muted-foreground">{t("team.intro.body", { price })}</p>
        <p className="text-xs text-muted-foreground/80">{t("team.intro.soon")}</p>
        {err && <Toast variant="destructive">{err}</Toast>}
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-center"
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate() }}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("team.namePlaceholder")}
            maxLength={120}
            disabled={!data.can_create}
            className="sm:max-w-xs"
          />
          <Button type="submit" disabled={!data.can_create || !name.trim() || create.isPending}>
            {create.isPending ? <Spinner /> : <UsersIcon className="h-4 w-4" />} {t("team.create")}
          </Button>
        </form>
        {!data.can_create && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>{t("team.createHint")}</span>
            <Link to="/billing" className="rounded-lg border border-gold/30 bg-gold/10 px-2.5 py-1 font-medium text-gold hover:bg-gold/15">
              {t("team.upgrade")}
            </Link>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

// ---------- in a workspace ----------

function Workspace({ data }: { data: TeamOut }) {
  const t = useT()
  const qc = useQueryClient()
  const ws = data.workspace!
  const isOwner = data.role === "owner"
  const [err, setErr] = useState<string | null>(null)
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["team"] })
    qc.invalidateQueries({ queryKey: ["my-limits"] })
    qc.invalidateQueries({ queryKey: ["knowledge"] })
  }
  const onError = (e: unknown) => setErr((e as Error).message)

  const leave = useMutation({
    mutationFn: () => api("/api/team/leave", { method: "POST" }),
    onSuccess: refresh, onError,
  })
  const del = useMutation({
    mutationFn: () => api("/api/team", { method: "DELETE" }),
    onSuccess: refresh, onError,
  })

  return (
    <div className="space-y-5">
      {err && <Toast variant="destructive">{err}</Toast>}

      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <WorkspaceName name={ws.name} editable={isOwner} onError={onError} />
            <Badge variant={isOwner ? "warn" : "default"}>{t(`team.role.${data.role}`)}</Badge>
          </div>
          <SeatsLimit data={data} isOwner={isOwner} onError={onError} />
          <PoolMeter data={data} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t("team.members")} right={<span className="text-xs text-muted-foreground">{data.members.length} {t("team.seats")}</span>} />
        <CardBody className="p-0">
          <MembersTable members={data.members} isOwner={isOwner} onError={onError} />
        </CardBody>
      </Card>

      {isOwner && <InvitesCard data={data} onError={onError} />}

      <SharedConnectors data={data} onError={onError} />

      <SharedKnowledge />

      <Card>
        <CardHeader title={t("team.danger")} />
        <CardBody className="flex flex-wrap gap-3">
          {!isOwner && (
            <Button
              variant="outline"
              disabled={leave.isPending}
              onClick={() => { if (window.confirm(t("team.leaveConfirm"))) leave.mutate() }}
            >
              {leave.isPending ? <Spinner /> : <UserMinusIcon className="h-4 w-4" />} {t("team.leave")}
            </Button>
          )}
          {isOwner && (
            <Button
              variant="destructive"
              disabled={del.isPending}
              onClick={() => { if (window.confirm(t("team.deleteConfirm", { name: ws.name }))) del.mutate() }}
            >
              {del.isPending ? <Spinner /> : <Trash2Icon className="h-4 w-4" />} {t("team.delete")}
            </Button>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

function WorkspaceName({ name, editable, onError }: { name: string; editable: boolean; onError: (e: unknown) => void }) {
  const t = useT()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const rename = useMutation({
    mutationFn: () => api<TeamOut>("/api/team", { method: "PATCH", body: { name: draft } }),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ["team"] }) },
    onError,
  })
  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <h2 className="text-[20px] font-medium tracking-tight">{name}</h2>
        {editable && (
          <button
            aria-label={t("team.rename")}
            title={t("team.rename")}
            onClick={() => { setDraft(name); setEditing(true) }}
            className="rounded-md p-1 text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <PencilIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    )
  }
  return (
    <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) rename.mutate() }}>
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={120} autoFocus className="max-w-xs" />
      <Button type="submit" size="sm" disabled={rename.isPending || !draft.trim()}>
        {rename.isPending ? <Spinner /> : <CheckIcon className="h-3.5 w-3.5" />} {t("team.save")}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
        <XIcon className="h-3.5 w-3.5" /> {t("team.cancel")}
      </Button>
    </form>
  )
}

function SeatsLimit({ data, isOwner, onError }: { data: TeamOut; isOwner: boolean; onError: (e: unknown) => void }) {
  const t = useT()
  const qc = useQueryClient()
  const limit = data.seats_limit ?? null
  const [draft, setDraft] = useState<string>(limit == null ? "" : String(limit))
  const [saved, setSaved] = useState(false)
  // Follow the server value (another tab, a refetch after save).
  useEffect(() => { setDraft(limit == null ? "" : String(limit)) }, [limit])
  const save = useMutation({
    mutationFn: () => api<TeamOut>("/api/team", {
      method: "PATCH", body: { seats_limit: draft.trim() === "" ? null : Number(draft) },
    }),
    onSuccess: () => { setSaved(true); qc.invalidateQueries({ queryKey: ["team"] }) },
    onError: (e) => { setSaved(false); onError(e) },
  })
  const price = data.seat_price_eur_month
  const seats = limit ?? data.seats_used
  const total = Math.round(seats * price * 100) / 100
  const dirty = draft.trim() !== (limit == null ? "" : String(limit))
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-muted-foreground" data-testid="team-seats">
      <span className="font-medium text-foreground">{t("team.seatsLimit")}</span>
      {isOwner ? (
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); if (dirty) save.mutate() }}
        >
          <Input
            type="number" min={1} max={200} inputMode="numeric"
            aria-label={t("team.seatsLimit")}
            placeholder={t("team.seatsLimit.none")}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setSaved(false) }}
            className="h-8 w-24 px-2.5 text-[12px]"
          />
          <Button type="submit" size="sm" variant="outline" disabled={!dirty || save.isPending}>
            {save.isPending ? <Spinner /> : <CheckIcon className="h-3.5 w-3.5" />} {t("team.save")}
          </Button>
          {saved && !dirty && <span className="text-[11px] text-mint">{t("team.seatsLimit.saved")}</span>}
        </form>
      ) : (
        <span className="text-foreground">{limit == null ? t("team.seatsLimit.none") : limit}</span>
      )}
      <span>{t("team.seatsLimit.used", { used: data.seats_used })}</span>
      {price > 0 && (
        <span className="tabular-nums">{t("team.seatsLimit.cost", { seats, price, total })}</span>
      )}
      {isOwner && <span className="basis-full text-[11px] text-muted-foreground/80">{t("team.seatsLimit.hint")}</span>}
    </div>
  )
}

function PoolMeter({ data }: { data: TeamOut }) {
  const t = useT()
  const pool = data.pool
  if (!pool) return null
  const pct = pool.cap_usd ? Math.min(1, pool.spent_usd / pool.cap_usd) : null
  const over = pct != null && pct >= 1
  const warn = pct != null && pct >= 0.8
  const color = over ? "bg-destructive" : warn ? "bg-warn" : "bg-primary"
  return (
    <div className="space-y-1.5" data-testid="team-pool">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[12px] text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">{t("team.pool")}</span>
          {pool.per_seat_usd != null && (
            <span className="ml-2">{t("team.poolSeats", { n: pool.seats, per: pool.per_seat_usd.toFixed(0) })}</span>
          )}
          {pool.credits_usd > 0 && <span className="ml-2">{t("team.poolCredits", { credits: pool.credits_usd.toFixed(2) })}</span>}
        </span>
        <span>
          <span className="font-medium text-foreground">${pool.spent_usd.toFixed(2)}</span>
          {pool.cap_usd != null ? ` / $${pool.cap_usd.toFixed(0)}` : ` · ${t("team.poolUnlimited")}`}
        </span>
      </div>
      {pct != null && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-foreground/[0.05]">
          <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.max(2, pct * 100)}%` }} />
        </div>
      )}
    </div>
  )
}

function MembersTable({ members, isOwner, onError }: { members: TeamMemberOut[]; isOwner: boolean; onError: (e: unknown) => void }) {
  const t = useT()
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: (userId: string) => api(`/api/team/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); qc.invalidateQueries({ queryKey: ["my-limits"] }) },
    onError,
  })
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-5 py-2.5 font-medium">{t("team.member.name")}</th>
            <th className="px-3 py-2.5 font-medium">{t("team.member.role")}</th>
            <th className="px-3 py-2.5 font-medium text-right">{t("team.member.spent")}</th>
            <th className="hidden px-3 py-2.5 font-medium sm:table-cell">{t("team.member.joined")}</th>
            {isOwner && <th className="px-5 py-2.5" />}
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.user_id} className="border-t border-border">
              <td className="px-5 py-2.5">
                <div className="font-medium">
                  {m.name} {m.is_me && <span className="text-xs text-muted-foreground">({t("team.you")})</span>}
                </div>
                <div className="text-[11px] text-muted-foreground">{m.email_masked}</div>
              </td>
              <td className="px-3 py-2.5">
                <Badge variant={m.role === "owner" ? "warn" : "muted"}>{t(`team.role.${m.role}`)}</Badge>
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums">${m.spent_month_usd.toFixed(2)}</td>
              <td className="hidden px-3 py-2.5 text-xs text-muted-foreground sm:table-cell">{timeAgo(m.joined_at)}</td>
              {isOwner && (
                <td className="px-5 py-2.5 text-right">
                  {m.role !== "owner" && !m.is_me && (
                    <Button
                      size="sm" variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => { if (window.confirm(t("team.removeConfirm", { name: m.name }))) remove.mutate(m.user_id) }}
                    >
                      <UserMinusIcon className="h-3.5 w-3.5" /> {t("team.remove")}
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function InvitesCard({ data, onError }: { data: TeamOut; onError: (e: unknown) => void }) {
  const t = useT()
  const qc = useQueryClient()
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<"member">("member")
  const invite = useMutation({
    mutationFn: () => api<TeamOut>("/api/team/invites", { method: "POST", body: { email, role } }),
    onSuccess: () => { setEmail(""); qc.invalidateQueries({ queryKey: ["team"] }) },
    onError,
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/team/invites/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }),
    onError,
  })
  return (
    <Card>
      <CardHeader title={t("team.invite.title")} subtitle={t("team.invite.hint")} />
      <CardBody className="space-y-4">
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-center"
          onSubmit={(e) => { e.preventDefault(); if (email.trim()) invite.mutate() }}
        >
          <Input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com" required className="sm:max-w-xs"
          />
          <select
            aria-label={t("team.member.role")}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value as "member")}
          >
            <option value="member">{t("team.role.member")}</option>
          </select>
          <Button type="submit" disabled={invite.isPending || !email.trim()}>
            {invite.isPending ? <Spinner /> : <SendIcon className="h-4 w-4" />} {t("team.invite.send")}
          </Button>
        </form>

        <div>
          <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">{t("team.invites")}</div>
          {data.invites.length === 0 && <p className="text-xs text-muted-foreground">{t("team.invite.none")}</p>}
          <ul className="divide-y divide-border">
            {data.invites.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{i.email}</div>
                  <div className="text-[11px] text-muted-foreground">{t("team.invite.sent", { when: timeAgo(i.created_at) })} · {t(`team.role.${i.role}`)}</div>
                </div>
                <Button size="sm" variant="ghost" disabled={revoke.isPending} onClick={() => revoke.mutate(i.id)}>
                  <XIcon className="h-3.5 w-3.5" /> {t("team.invite.revoke")}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </CardBody>
    </Card>
  )
}

function SharedConnectors({ data, onError }: { data: TeamOut; onError: (e: unknown) => void }) {
  const t = useT()
  const rows = data.shared_connectors ?? []
  return (
    <Card>
      <CardHeader
        title={t("team.conn.title")}
        subtitle={t("team.conn.sub")}
        right={<Link to="/connectors" className="text-xs text-muted-foreground hover:text-foreground">{t("team.conn.manage")}</Link>}
      />
      <CardBody>
        {rows.length === 0 && <p className="text-xs text-muted-foreground">{t("team.conn.none")}</p>}
        <ul className="divide-y divide-border" data-testid="team-shared-connectors">
          {rows.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <PlugIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate font-medium">{c.account_label || c.kind}</span>
                <span className="text-xs text-muted-foreground">{c.kind}</span>
                <span className="text-xs text-muted-foreground">
                  {c.mine ? `(${t("team.you")})` : t("team.conn.by", { name: c.owner_name })}
                </span>
              </div>
              {c.mine ? (
                <ShareModeControl connectorId={c.id} mode={c.share_mode} onError={onError} />
              ) : (
                <Badge variant={c.share_mode === "full" ? "ok" : "muted"}>{shareModeLabel(t, c.share_mode)}</Badge>
              )}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

function SharedKnowledge() {
  const t = useT()
  const me = auth.me()
  const qc = useQueryClient()
  const [err, setErr] = useState<string | null>(null)
  const list = useQuery({
    queryKey: ["knowledge", me?.id],
    queryFn: () => api<KnowledgeCollectionOut[]>("/api/knowledge/collections", { query: { user_id: me?.id } }),
    enabled: !!me?.id,
  })
  const toggle = useMutation({
    mutationFn: (vars: { id: string; share: boolean }) =>
      api(`/api/team/collections/${vars.id}/${vars.share ? "share" : "unshare"}`, { method: "POST" }),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ["knowledge"] }) },
    onError: (e) => setErr((e as Error).message),
  })
  const rows = list.data ?? []
  const mine = rows.filter((c) => !c.owner_name)
  const others = rows.filter((c) => !!c.owner_name)

  return (
    <Card>
      <CardHeader
        title={t("team.knowledge")}
        subtitle={t("team.knowledgeSub")}
        right={<Link to="/knowledge" className="text-xs text-muted-foreground hover:text-foreground">{t("team.knowledge.manage")}</Link>}
      />
      <CardBody className="space-y-4">
        {err && <Toast variant="destructive">{err}</Toast>}
        {list.isLoading && <Spinner />}
        {!list.isLoading && mine.length === 0 && <p className="text-xs text-muted-foreground">{t("team.knowledge.none")}</p>}
        <ul className="divide-y divide-border">
          {mine.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <BookOpenIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate font-medium">{c.name}</span>
              </div>
              <label className="flex shrink-0 cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                  checked={!!c.shared}
                  disabled={toggle.isPending}
                  onChange={(e) => toggle.mutate({ id: c.id, share: e.target.checked })}
                />
                {t("team.knowledge.shared")}
              </label>
            </li>
          ))}
        </ul>
        {others.length > 0 && (
          <div>
            <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">{t("team.knowledge.others")}</div>
            <ul className="divide-y divide-border">
              {others.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="flex min-w-0 items-center gap-2">
                    <BookOpenIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate font-medium">{c.name}</span>
                    <span className="text-xs text-muted-foreground">{t("team.knowledge.by", { name: c.owner_name ?? "…" })}</span>
                  </div>
                  <Badge variant="muted">{t("team.knowledge.readonly")}</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
